import moment from 'moment';
import { Quadrant } from '../enums/Quadrant';
import { Geolocation, LngLatGeolocation } from '../types';
import { BalloonEventsQuery } from '../types/BalloonEventsQuery';
import { BalloonEventModel } from './BalloonEventModel';
import { BoundingBox } from './BoundingBox';
import { EventDateModel } from './EventDateModel';
import { MetadataModel } from './';

export enum Fits {
  NO = -2,
  YES_NO_BBOX = -1,
  CENTER,
  YES_NE,
  YES_SE,
  YES_SW,
  YES_NW,
}

//"x-partner-api-key"

export interface BoundsShape {
  contains: (location: Geolocation) => boolean;
  getNorthEast: () => LngLatGeolocation;
  getNorthWest: () => LngLatGeolocation;
  getSouthWest: () => LngLatGeolocation;
  getSouthEast: () => LngLatGeolocation;
}

export class QueryModel {
  _amount = 20;
  _page = 0;

  _bbox: BoundsShape | null = null;
  _quadrants: (BoundsShape | null)[] = [null, null, null, null, null];
  _regionArea: string[] = [];
  _region: string[] = [];

  _text: string = '';
  _categories: string[] = [];
  _fromDate: Date | null = null;
  _untilDate: Date | null = null;
  _sources: string[] = [];
  _tags: string[] = [];
  _user: string[] = [];
  _providers: string[] = [];

  _mustHaveAtLeastOneImage: boolean = false;

  _createQuadrants = true;

  constructor(createQuadrants: boolean = true) {
    this._createQuadrants = createQuadrants;
  }

  set(query: BalloonEventsQuery) {
    this.amount = query.amount || 20;
    if (query.bbox) {
      if ('contains' in query.bbox) {
        this.bbox = query.bbox;
      } else {
        this.bbox = new BoundingBox(query.bbox.northEast, query.bbox.southWest);
      }
    } else query.bbox = undefined;
    this.text = query.text || '';
    this.categories = query.categories || [];
    this.dates = query.dates || null;
    this.sources = query.sources || [];
    this.tags = query.tags || [];
    this.user = query.user || [];
    this.providers = query.providers || [];
    this.regionArea = query.regionArea || [];
    this.region = query.region || [];
    this.mustHaveAtLeastOneImage = query.mustHaveAtLeastOneImage || false;
  }

  get(quadrant?: number): BalloonEventsQuery {
    const q = {} as BalloonEventsQuery;
    if (this._bbox) {
      if (quadrant === undefined || this._quadrants[quadrant] === undefined)
        q.bbox = this._bbox;
      else q.bbox = this._quadrants[quadrant]!;
    }
    if (this._text) q.text = this._text;
    if (this._categories) q.categories = this._categories;
    if (this._fromDate) q.dates = [this._fromDate];
    if (this._untilDate) q.dates?.push(this._untilDate);
    if (this._sources) q.sources = this._sources;
    if (this._tags) q.tags = this._tags;
    if (this._user) q.user = this._user;
    if (this._providers) q.providers = this._providers;
    if (this._regionArea) q.regionArea = this._regionArea;
    if (this._region) q.region = this._region;
    return q;
  }

  categoryModels(metadata: MetadataModel) {
    if (this.categories == null) return [];
    return this.categories.map((c) => metadata.getCategory(c));
  }

  get mustHaveAtLeastOneImage() {
    return this._mustHaveAtLeastOneImage;
  }

  set mustHaveAtLeastOneImage(mustHaveAtLeastOneImage: boolean) {
    this._mustHaveAtLeastOneImage = mustHaveAtLeastOneImage;
  }

  get quadrants() {
    return this._quadrants;
  }

  private get _centerQBbox() {
    return this._quadrants[Quadrant.CENTER];
  }

  private set _centerQBbox(bbox: BoundsShape | null) {
    this._quadrants[Quadrant.CENTER] = bbox;
  }

  private get _nwQBbox() {
    return this._quadrants[Quadrant.NW];
  }

  private set _nwQBbox(bbox: BoundsShape | null) {
    this._quadrants[Quadrant.NW] = bbox;
  }

  private get _swQBbox() {
    return this._quadrants[Quadrant.SW];
  }

  private set _swQBbox(bbox: BoundsShape | null) {
    this._quadrants[Quadrant.SW] = bbox;
  }

  private get _seQBbox() {
    return this._quadrants[Quadrant.SE];
  }

  private set _seQBbox(bbox: BoundsShape | null) {
    this._quadrants[Quadrant.SE] = bbox;
  }

  private get _neQBbox() {
    return this._quadrants[Quadrant.NE];
  }

  private set _neQBbox(bbox: BoundsShape | null) {
    this._quadrants[Quadrant.NE] = bbox;
  }

  public set amount(amount: number) {
    this._amount = amount;
  }

  get amount() {
    return this._amount;
  }

  set page(page: number) {
    this._page = page;
  }

  get page() {
    return this._page;
  }

  set sources(sources: string[]) {
    this._sources = sources || [];
  }

  get sources() {
    return this._sources;
  }

  get regionArea() {
    return this._regionArea;
  }

  set regionArea(regionArea: string[]) {
    this._regionArea = regionArea;
  }

  get region() {
    return this._region;
  }

  set region(region: string[]) {
    this._region = region;
  }

  set bbox(bbox: BoundsShape | null) {
    this._bbox = bbox;
    if (!this._createQuadrants) return;
    if (bbox == null) {
      this._nwQBbox = null;
      this._swQBbox = null;
      this._seQBbox = null;
      this._neQBbox = null;
      return;
    }
    let ne = bbox.getNorthEast();
    let sw = bbox.getSouthWest();

    let n = ne.lat;
    let s = sw.lat;
    let w = sw.lng;
    let e = ne.lng;

    let centerQN = n - (n - s) / 4;
    let centerQS = s + (n - s) / 4;
    let centerQW = w + (e - w) / 4;
    let centerQE = e - (e - w) / 4;

    let nwQN = n;
    let nwQS = s + (n - s) / 2;
    let nwQW = w;
    let nwQE = w + (e - w) / 2;

    let swQN = s + (n - s) / 2;
    let swQS = s;
    let swQW = w;
    let swQE = w + (e - w) / 2;

    let seQN = s + (n - s) / 2;
    let seQS = s;
    let seQW = w + (e - w) / 2;
    let seQE = e;

    let neQN = n;
    let neQS = s + (n - s) / 2;
    let neQW = w + (e - w) / 2;
    let neQE = e;

    this._centerQBbox = new BoundingBox(
      { lat: centerQN, lng: centerQE },
      { lat: centerQS, lng: centerQW }
    );

    this._nwQBbox = new BoundingBox(
      { lat: nwQN, lng: nwQE },
      { lat: nwQS, lng: nwQW }
    );
    this._swQBbox = new BoundingBox(
      { lat: swQN, lng: swQE },
      { lat: swQS, lng: swQW }
    );
    this._seQBbox = new BoundingBox(
      { lat: seQN, lng: seQE },
      { lat: seQS, lng: seQW }
    );
    this._neQBbox = new BoundingBox(
      { lat: neQN, lng: neQE },
      { lat: neQS, lng: neQW }
    );
  }

  public clearBbox() {
    this._bbox = null;
    this._quadrants = [null, null, null, null, null];
  }

  get bbox() {
    return this._bbox;
  }

  get nwQBbox() {
    return this._nwQBbox;
  }

  get swQBbox() {
    return this._swQBbox;
  }

  get seQBbox() {
    return this._seQBbox;
  }

  get neQBbox() {
    return this._neQBbox;
  }

  set text(text: string) {
    this._text = text == null ? '' : text.toLowerCase();
  }

  get text() {
    return this._text;
  }

  set categories(categories: string[] | null) {
    if (categories == null) categories = [];

    this._categories = categories;
  }

  get categories() {
    return this._categories;
  }

  get dates() {
    const dates = [];
    if (this._fromDate) dates.push(this._fromDate);
    if (this._untilDate) dates.push(this._untilDate);
    return dates;
  }

  set dates(dates: Date[] | null) {
    if (dates) {
      dates = dates.map((d) => {
        if (d instanceof Date) return d;
        return new Date(d);
      });
      dates = dates.sort((a, b) => a.getTime() - b.getTime());
      if (dates.length === 1)
        dates = [...dates, new Date(dates[0].getTime() + 24 * 60 * 60 * 1000)];
    }
    this._fromDate =
      dates != null && dates.length > 0
        ? moment(dates[0]).startOf('hour').toDate()
        : null;
    this._untilDate =
      dates != null && dates.length > 1
        ? moment(dates[1]).startOf('hour').toDate()
        : null;
  }

  get fromDate() {
    return this._fromDate || new Date();
  }

  get untilDate() {
    return this._untilDate;
  }

  get tags() {
    return this._tags;
  }

  set tags(tags: string[]) {
    this._tags = tags;
  }

  get user() {
    return this._user;
  }

  set user(user: string[]) {
    this._user = user || [];
  }

  get providers() {
    return this._providers;
  }

  set providers(providers: string[]) {
    this._providers = providers || [];
  }

  fits(event: BalloonEventModel) {
    const fitsDate = this._fitsDate(event);
    const fitsBbox = this._fitsBbox(event);
    const fitsCategories = this._fitsCategories(event);
    const fitsText = this._fitsTextSearch(event);
    const fitsSources = this._fitsSources(event);
    const fitsTags = this._fitsTags(event);
    const fitsUser = this._fitsUser(event);
    const fitsProviders = this._fitsProviders(event);
    const fitsRegionArea = this._fitsRegionArea(event);
    const fitsRegion = this._fitsRegion(event);

    const fits =
      fitsDate &&
      fitsBbox &&
      fitsCategories &&
      fitsText &&
      fitsSources &&
      fitsTags &&
      fitsUser &&
      fitsProviders &&
      fitsRegionArea &&
      fitsRegion;

    let fitsEnum = Fits.NO;
    if (fits) {
      if (this._bbox) {
        fitsEnum = this.getQuadrant(event) as Fits;
      } else fitsEnum = Fits.CENTER;
    }
    return fitsEnum;
  }

  private _fitsRegion(event: BalloonEventModel) {
    if (this.region == null || this._region.length === 0) return true;
    for (const region of event.regionAreas) {
      if (this._region.includes(region)) return true;
    }
    return false;
  }

  private _fitsRegionArea(event: BalloonEventModel) {
    if (this._regionArea == null || this._regionArea.length === 0) return true;
    for (const area of event.regionAreas) {
      if (this._regionArea.includes(area)) return true;
    }
    return false;
  }

  private _fitsFromDate(date: EventDateModel) {
    return this.fromDate.getTime() < date.start.getTime();
  }

  private _fitsUntilDate(date: EventDateModel) {
    if (this._untilDate == null) return true;
    return this.untilDate!.getTime() > date.start.getTime();
  }

  private _fitsDate(event: BalloonEventModel) {
    // No explicit date filter → mirror the default "upcoming only" the backend
    // applies (dateFrom ≈ now). Without this, past events fetched while a
    // date filter was active linger in the repository cache and stay on the map
    // after the filter is cleared. `timeUntilStart === null` is the model's own
    // "already ended" signal (future/ongoing events are non-null). Events with
    // no dates are kept (we can't place them in time).
    if (this.dates == null || this.dates.length === 0) {
      return event.dates.length === 0 || event.timeUntilStart !== null;
    }
    for (const date of event.dates) {
      const fitsFromDate = this._fitsFromDate(date);
      const fitsUntilDate = this._fitsUntilDate(date);

      if (fitsFromDate && fitsUntilDate) return true;
    }
    return false;
  }

  private _fitsCategories(event: BalloonEventModel) {
    if (this._categories == null || this._categories.length === 0) return true;
    for (const category of event.categories) {
      if (this._categories.includes(category.id)) return true;
    }
    return false;
  }

  private _fitsBbox(event: BalloonEventModel) {
    if (
      !event.location ||
      Number.isNaN(event.location.lat) ||
      Number.isNaN(event.location.lng)
    )
      return this._bbox ? false : true;
    return (
      event.location &&
      (!this._bbox || this._bbox.contains(event.location as Geolocation))
    );
  }

  private _isInQuadrant(event: BalloonEventModel, quadrant: BoundsShape) {
    return event.location && quadrant.contains(event.location as Geolocation);
  }

  getQuadrant(event: BalloonEventModel): number {
    for (let i = 0; i < this._quadrants.length; i++) {
      if (
        this._quadrants[i] &&
        this._isInQuadrant(event, this._quadrants[i]!)
      ) {
        return i;
      }
    }
    return Quadrant.Null;
  }

  private _fitsTextSearch(event: BalloonEventModel) {
    return (
      !this._text ||
      (event.title && event.title.toLowerCase().includes(this._text)) ||
      (event.teaser && event.teaser.includes(this._text)) ||
      (event.description && event.description.includes(this._text))
    );
  }

  private _fitsSources(event: BalloonEventModel) {
    return (
      this._sources.length === 0 ||
      this._sources.includes(event.provider?.shortName || '')
    );
  }

  private _fitsProviders(event: BalloonEventModel) {
    if (this._providers == null || this._providers.length === 0) return true;
    return this._providers.includes(event.provider?.shortName || '');
  }

  private _fitsTags(event: BalloonEventModel) {
    const fitsTags =
      this._tags.length === 0 || this._tags.some((t) => event.tags.includes(t));
    return fitsTags;
  }

  private _fitsUser(event: BalloonEventModel) {
    if (this._user == null || this._user.length === 0) return true;
    const slug = event.creator?.slug;
    return slug != null && this._user.includes(slug);
  }
}
